import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { Subject, merge } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, throttleTime } from 'rxjs/operators';

import { AlertService } from '../../services/alert.service';
import { ProgressIndicatorService } from '../../services/progress-indicator.service';
import { WatchfaceService } from '../../services/watchface.service';

import { environment } from '../../../environments/environment';

declare var $: any;

@Component({
    selector: 'watchface',
    templateUrl: './watchface.component.html',
    styleUrls: ['./watchface.component.scss']
})
export class WatchfaceComponent implements OnInit, OnDestroy {

    watchfacesStore: string;

    watchfaceId!: string | null;

    formGroup!: FormGroup;

    watchfaces: any[] = [];

    count!: number | null;

    countLeft!: number;

    countInProgress: number = 0;

    searchSubject!: Subject<string>;

    lastRequestReceived: number = Number.NEGATIVE_INFINITY;

    constructor(
        private route: ActivatedRoute,
        private alertService: AlertService,
        private progressIndicatorService: ProgressIndicatorService,
        private watchfaceService: WatchfaceService,
        private formBuilder: FormBuilder
    ) {
        this.watchfacesStore = environment.watchfacesStore;
    }

    ngOnInit() {

        this.searchSubject = new Subject<string>();

        merge(
            this.searchSubject.pipe(
                map(value => value.trim()),
                //filter(value => value.length > 0),
                distinctUntilChanged(),
                throttleTime(500)
            ),
            this.searchSubject.pipe(
                map(value => value.trim()),
                //filter(value => value.length > 0),
                distinctUntilChanged(),
                debounceTime(500)
            )
        ).pipe(
            map(value => value.trim()),
            //filter(value => value.length > 0),
            distinctUntilChanged()
        ).subscribe(
            (value) => {
                this.searchWatchfaces();
            }
        );

        this.formGroup = this.formBuilder.group({
            searchInput: [null],
            tagZeTimeCheckbox: [null],
            tagZeSport2Checkbox: [null],
            createdAfterInput: [null],
            createdBeforeInput: [null],
            statusPendingCheckbox: [null],
            statusModifyCheckbox: [null],
            statusApprovedCheckbox: [null],
            statusRejectedCheckbox: [null],
            minStarsSelect: [null],
            maxStarsSelect: [null],
            orderBySelect: [null]
        });

        this.route.paramMap.subscribe(
            (params) => {
                let watchfaceId: string | null = params.get('watchfaceId');
                if (watchfaceId && watchfaceId.match(/^\d+$/)) {
                    this.watchfaceId = watchfaceId;
                } else {
                    this.watchfaceId = null;
                }
                if (this.watchfaceId) {
                    //this.formGroup.controls.searchInput.patchValue(this.watchfaceId);
                    this.getWatchface(this.watchfaceId);
                } else {
                    //this.formGroup.controls.searchInput.patchValue(null);
                    this.searchWatchfaces();
                }
            }
        );
    }

    ngOnDestroy() {
    }

    get searchInProgress() {
        return this.countInProgress > 0;
    }

    searchInputKeyUp(search: string) {
        this.searchSubject.next(search);
    }

    getWatchface(watchfaceId: string) {
        this.countInProgress++;
        this.watchfaceService.getWatchface(watchfaceId).subscribe({
            next: (value: any) => {
                console.log('WatchfaceComponent.getWatchface.watchfaceService.getWatchface.value', value);
                this.countInProgress--;
                this.watchfaces = [value];
                this.count = null;
                this.countLeft = 0;
            },
            error: (error: any) => {
                console.log('WatchfaceComponent.getWatchface.watchfaceService.getWatchface.error', error);
                this.countInProgress--;
                this.alertService.danger('Error');
            }
        });
    }

    searchWatchfaces(skip?: number) {

        const requestDateTime: number = Date.now();

        let formGroup: FormGroup = this.formGroup;

        const parameters: any = {};
        parameters.skip = skip || 0;
        parameters.max = 8;

        let search = formGroup.value.searchInput;
        if (search && search.trim().length > 0) {
            parameters.search = search.trim();
        }

        let tags: string[] = [];
        if (formGroup.value.tagZeTimeCheckbox === true) {
            tags.push('zetime');
        }
        if (formGroup.value.tagZeSport2Checkbox === true) {
            tags.push('zesport2');
        }
        if (tags.length > 0) {
            parameters.tags = tags;
        }

        let createdAfterDate: Date | null = null;
        let createdAfterInput: string = formGroup.value.createdAfterInput;
        if (createdAfterInput != null) {
            try {
                createdAfterDate = new Date(createdAfterInput);
                if (isNaN(createdAfterDate.getTime())) {
                    let createdAfterFloat: number = parseFloat(createdAfterInput);
                    if (isNaN(createdAfterFloat)) {
                        createdAfterDate = null;
                    } else {
                        createdAfterDate = new Date(createdAfterFloat * 1000);
                        if (isNaN(createdAfterDate.getTime())) {
                            createdAfterDate = null;
                        }
                    }
                }
            } catch (e) {
                createdAfterDate = null;
            }
        }
        if (createdAfterDate) {
            parameters.from = Math.round(createdAfterDate.getTime() / 1000);
            formGroup.controls['createdAfterInput'].patchValue(createdAfterDate.toISOString().split('T')[0]);
        } else {
            formGroup.controls['createdAfterInput'].patchValue(null);
        }

        let createdBeforeDate: Date | null = null;
        let createdBeforeInput: string = formGroup.value.createdBeforeInput;
        if (createdBeforeInput != null) {
            try {
                createdBeforeDate = new Date(createdBeforeInput);
                if (isNaN(createdBeforeDate.getTime())) {
                    let createdBeforeFloat: number = parseFloat(createdBeforeInput);
                    if (isNaN(createdBeforeFloat)) {
                        createdBeforeDate = null;
                    } else {
                        createdBeforeDate = new Date(createdBeforeFloat * 1000);
                        if (isNaN(createdBeforeDate.getTime())) {
                            createdBeforeDate = null;
                        }
                    }
                }
            } catch (e) {
                createdBeforeDate = null;
            }
        }
        if (createdBeforeDate) {
            parameters.to = Math.round(createdBeforeDate.getTime() / 1000);
            formGroup.controls['createdBeforeInput'].patchValue(createdBeforeDate.toISOString().split('T')[0]);
        } else {
            formGroup.controls['createdBeforeInput'].patchValue(null);
        }

        let status: string[] = [];
        if (formGroup.value.statusPendingCheckbox === true) {
            status.push('pending');
        }
        if (formGroup.value.statusModifyCheckbox === true) {
            status.push('modify');
        }
        if (formGroup.value.statusApprovedCheckbox === true) {
            status.push('approved');
        }
        if (formGroup.value.statusRejectedCheckbox === true) {
            status.push('rejected');
        }
        if (status.length > 0) {
            parameters.status = status;
        }

        let minStars: string = formGroup.value.minStarsSelect;
        if (['0', '1', '2', '3', '4', '5'].includes(minStars)) {
            parameters.rmin = minStars;
        }

        let maxStars: string = formGroup.value.maxStarsSelect;
        if (['0', '1', '2', '3', '4', '5'].includes(maxStars)) {
            parameters.rmax = maxStars;
        }

        let orderby: string = formGroup.value.orderBySelect;
        if (['datetime', 'downloads', 'score'].includes(orderby)) {
            parameters.orderby = orderby;
        }

        this.countInProgress++;
        this.watchfaceService.getAdminWatchfaces(parameters).subscribe({
            next: (value: any) => {
                console.log('WatchfaceComponent.searchWatchfaces.watchfaceService.getAdminWatchfaces.value', value);
                this.countInProgress--;
                if (this.lastRequestReceived < requestDateTime) {
                    this.lastRequestReceived = requestDateTime;
                    if (value.skip == 0) {
                        this.watchfaces = value.data;
                    } else {
                        this.watchfaces.push(...value.data);
                    }
                    this.count = value.count;
                    this.countLeft = value.count - value.skip - value.data.length;
                    setTimeout(() => this.autoSearchWatchfaces(), 0);
                }
            },
            error: (error: any) => {
                console.log('WatchfaceComponent.searchWatchfaces.watchfaceService.getAdminWatchfaces.error', error);
                this.countInProgress--;
                this.alertService.danger('Error');
            }
        });
    }

    clickApproveWatchface(watchface: any) {
        this.progressIndicatorService.progress = true;
        this.watchfaceService.approveWatchface(watchface.id).subscribe({
            next: (value: any) => {
                console.log('WatchfaceComponent.clickApproveWatchface.watchfaceService.approveWatchface.value', value);
                this.progressIndicatorService.progress = false;
                this.alertService.success('Success');
                watchface.status = 'APPROVED';
            },
            error: (error: any) => {
                console.log('WatchfaceComponent.clickApproveWatchface.watchfaceService.approveWatchface.error', error);
                this.progressIndicatorService.progress = false;
                this.alertService.danger('Error');
            }
        });
    }

    clickRejectWatchface(watchface: any) {
        this.progressIndicatorService.progress = true;
        this.watchfaceService.rejectWatchface(watchface.id).subscribe({
            next: (value: any) => {
                console.log('WatchfaceComponent.clickRejectWatchface.watchfaceService.rejectWatchface.value', value);
                this.progressIndicatorService.progress = false;
                this.alertService.success('Success');
                watchface.status = 'REJECTED';
            },
            error: (error: any) => {
                console.log('WatchfaceComponent.clickRejectWatchface.watchfaceService.rejectWatchface.error', error);
                this.progressIndicatorService.progress = false;
                this.alertService.danger('Error');
            }
        });
    }

    @HostListener('window:scroll', [])
    autoSearchWatchfaces() {
        if (this.countLeft > 0 && !this.searchInProgress) {
            let documentHeight: number = $(document).height();
            let windowheight: number = $(window).height();
            let scrollTop: number = $(window).scrollTop();
            if (documentHeight - scrollTop < windowheight * 3) {
                this.searchWatchfaces(this.watchfaces.length);
            }
        }
    }
}
